import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import _, { debounce, keyBy } from "lodash";
import { FeatureTracking, SketchTool } from "./useSketchEvents";
import { LayerFieldDefinition } from "../../queries/maps";
import Sketch from "@arcgis/core/widgets/Sketch";
import * as equalsOperator from "@arcgis/core/geometry/operators/equalsOperator";
import { getSymbolForGeometryType } from "../../utils/arcgisHelper";

export enum HistoryOperationType {
  FieldDefinitions = "field_definitions",
  FeatureAdd = "feature_add",
  FeatureDelete = "feature_delete",
  FeatureUpdate = "feature_update", // For when both geometry and attributes change
  FeatureGeometryUpdate = "feature_geometry_update",
  FeatureAttributeUpdate = "feature_attribute_update",
  FeatureBatch = "feature_batch",
}

type FieldDefinitionsHistoryRecord = {
  type: HistoryOperationType.FieldDefinitions;
  prevFieldDefinitions: LayerFieldDefinition[];
  nextFieldDefinitions: LayerFieldDefinition[];
};
type FeatureAddHistoryRecord = {
  type: HistoryOperationType.FeatureAdd;
  graphicId: string;
  graphic: Graphic;
};
type FeatureDeleteHistoryRecord = {
  type: HistoryOperationType.FeatureDelete;
  graphicId: string;
  graphic: Graphic;
};
type FeatureUpdateHistoryRecord = {
  type: HistoryOperationType.FeatureUpdate;
  graphicId: string;
  prevGeometry: __esri.GeometryUnion;
  nextGeometry: __esri.GeometryUnion;
  prevAttributes: Record<string, any>;
  nextAttributes: Record<string, any>;
};
type FeatureGeometryUpdateHistoryRecord = {
  type: HistoryOperationType.FeatureGeometryUpdate;
  graphicId: string;
  prevGeometry: __esri.GeometryUnion;
  nextGeometry: __esri.GeometryUnion;
};
type FeatureAttributeUpdateHistoryRecord = {
  type: HistoryOperationType.FeatureAttributeUpdate;
  graphicId: string;
  fieldId: string | number;
  prevValue: any;
  nextValue: any;
};
type FeatureBatchHistoryRecord = {
  type: HistoryOperationType.FeatureBatch;
  records: HistoryRecord[];
};

export type HistoryRecord =
  | FieldDefinitionsHistoryRecord
  | FeatureAddHistoryRecord
  | FeatureDeleteHistoryRecord
  | FeatureUpdateHistoryRecord
  | FeatureGeometryUpdateHistoryRecord
  | FeatureAttributeUpdateHistoryRecord
  | FeatureBatchHistoryRecord;

interface UseFeatureEditorHistoryOptions {
  editLayerRef: MutableRefObject<GraphicsLayer | null>;
  sketchRef: MutableRefObject<Sketch | null>;
  fieldDefinitions: LayerFieldDefinition[];
  setFeatureTracking: (tracking: FeatureTracking | ((prev: FeatureTracking) => FeatureTracking)) => void;
  setFieldDefinitions: (
    definitions: LayerFieldDefinition[] | ((prev: LayerFieldDefinition[]) => LayerFieldDefinition[]),
  ) => void;
  readdHandlers: (selectedTool: SketchTool) => any[];
  selectedFeatures: __esri.Graphic[];
  sketchCanUndo: boolean;
  sketchCanRedo: boolean;
  sketchTool: SketchTool;
}

export function useFeatureEditorHistory({
  editLayerRef,
  sketchRef,
  fieldDefinitions,
  setFeatureTracking,
  setFieldDefinitions,
  readdHandlers,
  selectedFeatures,
  sketchCanUndo,
  sketchCanRedo,
  sketchTool,
}: UseFeatureEditorHistoryOptions) {
  const [historyStack, setHistoryStack] = useState<HistoryRecord[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedo, setIsUndoRedo] = useState(false);

  const canUndo = historyIndex >= 0 || sketchCanUndo;
  const canRedo = historyIndex < historyStack.length - 1 || sketchCanRedo;

  const prevFieldDefinitionsRef = useRef<LayerFieldDefinition[]>([]);
  const prevFeaturesRef = useRef<Map<string, { attributes: Record<string, any>; geometry: __esri.GeometryUnion }>>(
    new Map(),
  );

  const originalFieldDefinitionsRef = useRef<LayerFieldDefinition[]>([]);
  const originalFeaturesRef = useRef<Map<string, { attributes: Record<string, any>; geometry: __esri.GeometryUnion }>>(
    new Map(),
  );

  const isBatchingRef = useRef(false);
  const batchedOperationsRef = useRef<HistoryRecord[]>([]);
  const [changedFeatureIds, setChangedFeatureIds] = useState(new Set<string>());
  const pendingChangesRef = useRef(new Set<string>());

  const initialStateLoadedRef = useRef(false);

  const markFeatureAsChanged = (graphicId: string) => {
    const newSet = new Set(changedFeatureIds).add(graphicId);
    setIsUndoRedo(false);
    setChangedFeatureIds(newSet);
  };
  const processChangesDebounced = useCallback(
    debounce(() => {
      if (pendingChangesRef.current.size === 0) return;

      // Transfer pending changes to the active changes set
      const newSet = new Set([...Array.from(changedFeatureIds), ...Array.from(pendingChangesRef.current)]);
      setIsUndoRedo(false);
      setChangedFeatureIds(newSet);
      pendingChangesRef.current.clear();
    }, 500),
    [],
  );
  const markFeatureAsChangedDebounced = (graphicId: string) => {
    pendingChangesRef.current.add(graphicId);
    processChangesDebounced();
  };

  // Track field definition changes
  useEffect(() => {
    if (isUndoRedo) {
      setIsUndoRedo(false);
      return;
    }

    // Skip the first load
    if (!initialStateLoadedRef.current) {
      prevFieldDefinitionsRef.current = _.cloneDeep(fieldDefinitions);
      originalFieldDefinitionsRef.current = _.cloneDeep(fieldDefinitions);
      return;
    }

    // If there's a difference, add to the history stack
    if (!_.isEqual(prevFieldDefinitionsRef.current, fieldDefinitions)) {
      const newRecord: FieldDefinitionsHistoryRecord = {
        type: HistoryOperationType.FieldDefinitions,
        prevFieldDefinitions: _.cloneDeep(prevFieldDefinitionsRef.current),
        nextFieldDefinitions: _.cloneDeep(fieldDefinitions),
      };

      addToHistory(newRecord);
      prevFieldDefinitionsRef.current = _.cloneDeep(fieldDefinitions);
    }
  }, [fieldDefinitions]);

  // Track feature changes
  useEffect(() => {
    if (!editLayerRef.current || isUndoRedo || changedFeatureIds.size === 0) return;

    const currentGraphics = editLayerRef.current.graphics.toArray();
    const currentGraphicsMap = new Map<string, { attributes: Record<string, any>; geometry: __esri.GeometryUnion }>();

    // Only process graphics that were marked as changed
    const changedGraphics = currentGraphics.filter((g) => changedFeatureIds.has(g.attributes?.graphicId));
    const changedGraphicsMap = keyBy(changedGraphics, (g) => g.attributes?.graphicId) as Record<string, Graphic>;
    changedGraphics.forEach((graphic) => {
      const graphicId = graphic.attributes?.graphicId;
      if (!graphicId) return;

      currentGraphicsMap.set(graphicId, {
        attributes: _.cloneDeep(graphic.attributes),
        geometry: graphic.geometry!.clone(),
      });
    });

    // Skip the first load
    if (!initialStateLoadedRef.current) {
      prevFeaturesRef.current = currentGraphicsMap;
      originalFeaturesRef.current = new Map(currentGraphicsMap);
      initialStateLoadedRef.current = true;
      return;
    }

    // Process changes as before, but only for changed features
    const addedRecords: HistoryRecord[] = [];
    const updatedRecords: HistoryRecord[] = [];
    const deletedRecords: HistoryRecord[] = [];

    // Find added graphics
    Array.from(changedFeatureIds).forEach((graphicId) => {
      const graphic = changedGraphicsMap[graphicId];

      if (graphic && !prevFeaturesRef.current.has(graphicId)) {
        addedRecords.push({
          type: HistoryOperationType.FeatureAdd,
          graphicId,
          graphic: graphic.clone(),
        });
      }
    });

    // Find updated and deleted graphics
    Array.from(changedFeatureIds).forEach((graphicId) => {
      if (!prevFeaturesRef.current.has(graphicId)) return;

      const prevFeature = prevFeaturesRef.current.get(graphicId)!;
      const currentGraphic = changedGraphicsMap[graphicId];

      if (!currentGraphic) {
        // Feature was deleted
        const deletedGraphic = new Graphic({
          attributes: _.cloneDeep(prevFeature.attributes),
          geometry: prevFeature.geometry.clone(),
        });

        deletedRecords.push({
          type: HistoryOperationType.FeatureDelete,
          graphicId,
          graphic: deletedGraphic,
        });
        return;
      }

      const currentFeature = {
        attributes: currentGraphic.attributes,
        geometry: currentGraphic.geometry!,
      };

      const didAttributesChange = !_.isEqual(prevFeature.attributes, currentFeature.attributes);
      const didGeometryChange = !equalsOperator.execute(prevFeature.geometry, currentFeature.geometry);

      if (didAttributesChange && didGeometryChange) {
        updatedRecords.push({
          type: HistoryOperationType.FeatureUpdate,
          graphicId,
          prevGeometry: prevFeature.geometry.clone(),
          nextGeometry: currentFeature.geometry.clone(),
          prevAttributes: _.cloneDeep(prevFeature.attributes),
          nextAttributes: _.cloneDeep(currentFeature.attributes),
        });
      } else if (didAttributesChange) {
        updatedRecords.push({
          type: HistoryOperationType.FeatureAttributeUpdate,
          graphicId,
          fieldId: "*", // Wildcard to indicate all fields changed
          prevValue: _.cloneDeep(prevFeature.attributes),
          nextValue: _.cloneDeep(currentFeature.attributes),
        });
      } else if (didGeometryChange) {
        updatedRecords.push({
          type: HistoryOperationType.FeatureGeometryUpdate,
          graphicId,
          prevGeometry: prevFeature.geometry.clone(),
          nextGeometry: currentFeature.geometry.clone(),
        });
      }
    });

    // Add all changes to history if there are any
    const allRecords = [...addedRecords, ...updatedRecords, ...deletedRecords];
    if (allRecords.length > 0) {
      if (isBatchingRef.current) {
        batchedOperationsRef.current.push(...allRecords);
      } else if (allRecords.length === 1) {
        addToHistory(allRecords[0]);
      } else {
        // If multiple records, batch them together
        addToHistory({
          type: HistoryOperationType.FeatureBatch,
          records: allRecords,
        });
      }
    }

    // Update ref for the next comparison - only for changed features
    Array.from(changedFeatureIds).forEach((graphicId) => {
      const graphic = currentGraphicsMap.get(graphicId);

      if (graphic?.geometry) {
        prevFeaturesRef.current.set(graphicId, {
          attributes: _.cloneDeep(graphic.attributes),
          geometry: graphic.geometry.clone(),
        });
      } else {
        // If graphic is no longer present, remove from tracking
        prevFeaturesRef.current.delete(graphicId);
      }
    });

    // Clear the changed features set after processing
    setChangedFeatureIds(new Set());
  }, [editLayerRef.current?.graphics.length, changedFeatureIds.size]);

  const addToHistory = (record: HistoryRecord) => {
    if (isUndoRedo) return;

    // If we're in the middle of the history stack, remove everything after current index
    setHistoryStack((prev) => {
      const undoStack = prev.slice(0, historyIndex + 1);
      // Need to update the next of the previous and the prev of the record to be correct
      if ("prevFieldDefinitions" in record) {
        // Field Definition Update
        if (historyIndex < 0) {
          record.prevFieldDefinitions = originalFieldDefinitionsRef.current;
        } else {
          const lastMatchingStackAction = undoStack.findLast(
            (action) => "prevFieldDefinition" in action,
          ) as FieldDefinitionsHistoryRecord;
          if (lastMatchingStackAction) record.prevFieldDefinitions = lastMatchingStackAction.nextFieldDefinitions;
        }
      } else if ("prevGeometry" in record) {
        // Geometry Update
        if (historyIndex < 0) {
          if (originalFeaturesRef.current.has(record.graphicId)) {
            record.prevGeometry = originalFeaturesRef.current.get(record.graphicId)!.geometry.clone();
          }
        } else {
          const lastMatchingStackAction = undoStack.findLast(
            (action) => "prevGeometry" in action && action.graphicId === record.graphicId,
          ) as FeatureGeometryUpdateHistoryRecord | undefined;
          if (lastMatchingStackAction) record.prevGeometry = lastMatchingStackAction.nextGeometry;
        }
      } else if ("prevValue" in record) {
        // Attribute Update
        if (historyIndex < 0) {
          if (originalFeaturesRef.current.has(record.graphicId)) {
            record.prevValue = originalFeaturesRef.current.get(record.graphicId)!.attributes[record.fieldId];
          }
        } else {
          const lastMatchingStackAction = undoStack.findLast(
            (action) => "prevValue" in action && action.graphicId === record.graphicId,
          ) as FeatureAttributeUpdateHistoryRecord | undefined;
          if (lastMatchingStackAction) record.prevValue = lastMatchingStackAction.nextValue;
        }
      }
      return [...undoStack, record];
    });
    setHistoryIndex((prev) => prev + 1);
  };

  const startBatch = () => {
    isBatchingRef.current = true;
    batchedOperationsRef.current = [];
  };

  // End batching and record as a single history entry
  const endBatch = () => {
    if (batchedOperationsRef.current.length > 0) {
      addToHistory({
        type: HistoryOperationType.FeatureBatch,
        records: batchedOperationsRef.current,
      });
    }
    isBatchingRef.current = false;
    batchedOperationsRef.current = [];
  };

  // Cancel batching without recording
  const cancelBatch = () => {
    isBatchingRef.current = false;
    batchedOperationsRef.current = [];
  };

  const executeUndo = (record: HistoryRecord): boolean => {
    switch (record.type) {
      case HistoryOperationType.FieldDefinitions:
        setFieldDefinitions(record.prevFieldDefinitions);
        return true;

      case HistoryOperationType.FeatureAdd:
        // Remove the added graphic
        if (!editLayerRef.current) return false;

        const graphicsToRemove = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );

        if (graphicsToRemove.length <= 0) return false;
        editLayerRef.current.graphics.removeMany(graphicsToRemove);

        // Update tracking
        setFeatureTracking((prev) => {
          const newTracking = { ...prev };
          delete newTracking[record.graphicId];
          return newTracking;
        });
        return true;

      case HistoryOperationType.FeatureDelete:
        // Re-add the deleted graphic
        if (!editLayerRef.current) return false;
        if (record.graphic.geometry) record.graphic.symbol = getSymbolForGeometryType(record.graphic.geometry.type);
        editLayerRef.current.graphics.add(record.graphic);

        // Update tracking
        setFeatureTracking((prev) => {
          const featureId = record.graphic.attributes?.featureId;
          return {
            ...prev,
            [record.graphicId]: {
              ...(featureId ? { featureId } : {}),
              ...(prev[record.graphicId] || {}),
              isDeleted: false,
            },
          };
        });
        return true;

      case HistoryOperationType.FeatureUpdate:
        // Restore previous geometry and attributes
        if (!editLayerRef.current) return false;

        const graphicsToUpdate = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );

        if (graphicsToUpdate.length <= 0) return false;
        graphicsToUpdate.getItemAt(0)!.geometry = record.prevGeometry;
        graphicsToUpdate.getItemAt(0)!.attributes = record.prevAttributes;

        // Update tracking if needed
        {
          const featureId = graphicsToUpdate.getItemAt(0)!.attributes?.featureId;
          if (!featureId) return true;
          setFeatureTracking((prev) => ({
            ...prev,
            [record.graphicId]: {
              ...(prev[record.graphicId] || {}),
              featureId,
              isModified: true,
            },
          }));
        }
        return true;
      case HistoryOperationType.FeatureGeometryUpdate:
        // Restore previous geometry
        if (!editLayerRef.current) return false;

        const graphicsToUpdateGeom = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );
        if (graphicsToUpdateGeom.length <= 0) return false;
        graphicsToUpdateGeom.getItemAt(0)!.geometry = record.prevGeometry;

        // Update tracking if needed
        const featureId = graphicsToUpdateGeom.getItemAt(0)!.attributes?.featureId;
        if (!featureId) return true;
        setFeatureTracking((prev) => ({
          ...prev,
          [record.graphicId]: {
            ...(prev[record.graphicId] || {}),
            featureId,
            isModified: true,
          },
        }));
        return true;

      case HistoryOperationType.FeatureAttributeUpdate:
        // Restore previous attribute value(s)
        if (!editLayerRef.current) return false;

        const graphicsToUpdateAttr = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );

        if (graphicsToUpdateAttr.length > 0) {
          if (record.fieldId === "*") {
            // Full attributes update
            graphicsToUpdateAttr.getItemAt(0)!.attributes = record.prevValue;
          } else {
            // Single field update
            graphicsToUpdateAttr.getItemAt(0)!.attributes[record.fieldId] = record.prevValue;
          }

          // Update tracking if needed
          const featureId = graphicsToUpdateAttr.getItemAt(0)!.attributes?.featureId;
          if (!featureId) return true;
          setFeatureTracking((prev) => ({
            ...prev,
            [record.graphicId]: {
              ...(prev[record.graphicId] || {}),
              featureId,
              isModified: true,
            },
          }));
          return true;
        }
        return false;

      case HistoryOperationType.FeatureBatch:
        // Execute each operation in the batch in reverse order
        let success = true;
        for (let i = record.records.length - 1; i >= 0; i--) {
          success = executeUndo(record.records[i]) && success;
        }
        return success;

      default:
        console.error("Unknown history record type", record);
        return false;
    }
  };

  const executeRedo = (record: HistoryRecord): boolean => {
    switch (record.type) {
      case HistoryOperationType.FieldDefinitions:
        setFieldDefinitions(record.nextFieldDefinitions);
        return true;

      case HistoryOperationType.FeatureAdd:
        // Re-add the feature
        if (!editLayerRef.current) return false;
        if (record.graphic.geometry) record.graphic.symbol = getSymbolForGeometryType(record.graphic.geometry.type);
        editLayerRef.current.graphics.add(record.graphic);

        // Update tracking
        setFeatureTracking((prev) => {
          const featureId = record.graphic.attributes?.featureId;
          return {
            ...prev,
            [record.graphicId]: {
              ...(featureId ? { featureId } : {}),
              isNew: !featureId,
            },
          };
        });
        return true;

      case HistoryOperationType.FeatureDelete:
        // Delete the feature again
        if (!editLayerRef.current) return false;

        const graphicsToRemove = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );

        if (graphicsToRemove.length > 0) {
          editLayerRef.current.graphics.removeMany(graphicsToRemove);

          // Update tracking
          const featureId = record.graphic.attributes?.featureId;
          if (featureId) {
            setFeatureTracking((prev) => ({
              ...prev,
              [record.graphicId]: {
                ...(prev[record.graphicId] || {}),
                featureId,
                isDeleted: true,
              },
            }));
          } else {
            setFeatureTracking((prev) => {
              const newTracking = { ...prev };
              delete newTracking[record.graphicId];
              return newTracking;
            });
          }
          return true;
        }
        return false;

      case HistoryOperationType.FeatureUpdate:
        // Apply the next geometry and attributes
        if (!editLayerRef.current) return false;

        const graphicsToUpdate = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );

        if (graphicsToUpdate.length <= 0) return false;
        graphicsToUpdate.getItemAt(0)!.geometry = record.nextGeometry;
        graphicsToUpdate.getItemAt(0)!.attributes = record.nextAttributes;

        // Update tracking if needed
        {
          const featureId = graphicsToUpdate.getItemAt(0)!.attributes?.featureId;
          if (!featureId) return true;
          setFeatureTracking((prev) => ({
            ...prev,
            [record.graphicId]: {
              ...(prev[record.graphicId] || {}),
              featureId,
              isModified: true,
            },
          }));
        }
        return true;

      case HistoryOperationType.FeatureGeometryUpdate:
        // Apply the next geometry
        if (!editLayerRef.current) return false;

        const graphicsToUpdateGeom = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );

        if (graphicsToUpdateGeom.length <= 0) return false;
        graphicsToUpdateGeom.getItemAt(0)!.geometry = record.nextGeometry;

        // Update tracking if needed
        const featureId = graphicsToUpdateGeom.getItemAt(0)!.attributes?.featureId;
        if (!featureId) return true;
        setFeatureTracking((prev) => ({
          ...prev,
          [record.graphicId]: {
            ...(prev[record.graphicId] || {}),
            featureId,
            isModified: true,
          },
        }));
        return true;

      case HistoryOperationType.FeatureAttributeUpdate:
        // Apply the next attribute value(s)
        if (!editLayerRef.current) return false;

        const graphicsToUpdateAttr = editLayerRef.current.graphics.filter(
          (g) => g.attributes?.graphicId === record.graphicId,
        );

        if (graphicsToUpdateAttr.length > 0) {
          if (record.fieldId === "*") {
            // Full attributes update
            graphicsToUpdateAttr.getItemAt(0)!.attributes = record.nextValue;
          } else {
            // Single field update
            graphicsToUpdateAttr.getItemAt(0)!.attributes[record.fieldId] = record.nextValue;
          }

          // Update tracking if needed
          const featureId = graphicsToUpdateAttr.getItemAt(0)!.attributes?.featureId;
          if (!featureId) return true;
          setFeatureTracking((prev) => ({
            ...prev,
            [record.graphicId]: {
              ...(prev[record.graphicId] || {}),
              featureId,
              isModified: true,
            },
          }));
          return true;
        }
        return false;

      case HistoryOperationType.FeatureBatch:
        // Execute each operation in the batch in original order
        let success = true;
        for (let i = 0; i < record.records.length; i++) {
          success = executeRedo(record.records[i]) && success;
        }
        return success;

      default:
        console.error("Unknown history record type", record);
        return false;
    }
  };

  const undo = () => {
    if (!canUndo) return false;
    const recordToUndo = historyStack[historyIndex];
    setIsUndoRedo(true);
    const success = executeUndo(recordToUndo);
    setHistoryIndex((prev) => prev - 1);
    readdHandlers(sketchTool);
    return success;
  };

  const redo = () => {
    if (!canRedo) return false;
    const recordToRedo = historyStack[historyIndex + 1];
    setIsUndoRedo(true);
    const success = executeRedo(recordToRedo);
    setHistoryIndex((prev) => prev + 1);
    readdHandlers(sketchTool);
    return success;
  };

  const handleUndo = () => {
    if (!canUndo) return;
    if (selectedFeatures.length && sketchCanUndo) {
      // If we're currently editing a feature, undo that
      sketchRef.current?.undo();
      return true;
    }
    sketchRef.current?.complete();
    undo();
  };

  const handleRedo = () => {
    if (!canRedo) return;
    if (selectedFeatures.length && sketchCanRedo) {
      // If we're currently editing a feature, redo that
      sketchRef.current?.redo();
      return true;
    }
    sketchRef.current?.complete();
    redo();
  };

  const clearHistory = () => {
    setHistoryStack([]);
    setHistoryIndex(-1);
  };

  return {
    undo,
    redo,
    handleUndo,
    handleRedo,
    canUndo,
    canRedo,
    startBatch,
    endBatch,
    cancelBatch,
    clearHistory,
    markFeatureAsChanged,
    markFeatureAsChangedDebounced,
  };
}
